import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAsterMonitorAlerts, getAsterMonitorHealth, getAsterExchangeAlerts } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const DIAMOND_ADDRESS = '0x1b6F2d3844C6ae7D56ceb3C3643b9060ba28FEb0';

const STAGE = {
  pending:   { labelKey: 'asterMonitor.stagePendingLabel', color: '#f59e0b', emoji: '🟡' },
  mined:     { labelKey: 'asterMonitor.stageMinedLabel',    color: '#f97316', emoji: '🟠' },
  confirmed: { labelKey: 'asterMonitor.stageConfirmedLabel', color: '#10b981', emoji: '🟢' },
};

const FUNCTION_KEY = {
  addPair: 'asterMonitor.fnAddPair',
  removePair: 'asterMonitor.fnRemovePair',
  updatePairStatus: 'asterMonitor.fnUpdatePairStatus',
};

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function shortAddr(a) {
  if (!a) return '—';
  return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

const txUrl = (tx) => `https://bscscan.com/tx/${tx}`;
const addrUrl = (a) => `https://bscscan.com/address/${a}`;

export default function AsterMonitorPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [alerts, setAlerts] = useState([]);
  const [exchangeAlerts, setExchangeAlerts] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const [{ alerts }, { alerts: exAlerts }, health] = await Promise.all([
        getAsterMonitorAlerts({ days: 14, stage: stageFilter || undefined }),
        getAsterExchangeAlerts({ days: 14 }).catch(() => ({ alerts: [] })),
        getAsterMonitorHealth().catch(() => null),
      ]);
      setAlerts(alerts || []);
      setExchangeAlerts(exAlerts || []);
      setHealth(health);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [stageFilter]);

  // 5s poll — faster than the staking page's 30s, since "confirmed" here
  // means as-soon-as-detected rather than a slow-moving aggregate.
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const bscHealth = health && health.bsc;
  const connected = bscHealth && bscHealth.connected;
  const stale = bscHealth && bscHealth.secondsSinceLastBlock != null && bscHealth.secondsSinceLastBlock > 60;
  const healthColor = !connected ? 'var(--text-dim)' : stale ? '#f59e0b' : '#10b981';
  const healthLabel = !connected ? t('asterMonitor.healthDisconnected') : stale ? t('asterMonitor.healthStale') : t('asterMonitor.healthListening');
  const mempoolActive = bscHealth && bscHealth.eventCounts && (bscHealth.eventCounts['bsc:pending'] || 0) > 0;

  const exInfoHealth = health && health.exchangeInfo;
  const exInfoHealthy = exInfoHealth && exInfoHealth.lastPollAt && (Date.now() - new Date(exInfoHealth.lastPollAt).getTime()) < 60000;
  const exInfoColor = !exInfoHealth ? 'var(--text-dim)' : exInfoHealth.lastError ? '#dc2626' : exInfoHealthy ? '#10b981' : '#f59e0b';
  const exInfoLabel = !exInfoHealth ? t('asterMonitor.healthDisconnected') : exInfoHealth.lastError ? t('asterMonitor.healthError', { error: exInfoHealth.lastError }) : exInfoHealthy ? t('asterMonitor.healthRunning') : t('asterMonitor.healthStale');

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>{t('asterMonitor.title')}</h2>
          <p style={s.subtitle}>
            {t('asterMonitor.subtitle')}{' '}
            <a href={addrUrl(DIAMOND_ADDRESS)} target="_blank" rel="noopener noreferrer" style={s.txLink}>{shortAddr(DIAMOND_ADDRESS)} ↗</a>
          </p>
        </div>
      </div>

      <div style={s.healthRow}>
        <div style={s.chip}>
          <span style={{ ...s.dot, background: healthColor }} />
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{t('asterMonitor.chainLabel')}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{healthLabel}</span>
        </div>
        <div style={{ ...s.chip, opacity: mempoolActive ? 1 : 0.6 }}>
          <span style={{ ...s.dot, background: mempoolActive ? '#f59e0b' : 'var(--text-dim)' }} />
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
            {t('asterMonitor.mempoolLabel', { status: mempoolActive ? t('asterMonitor.mempoolActive') : t('asterMonitor.mempoolInactive') })}
          </span>
        </div>
        <div style={s.chip}>
          <span style={{ ...s.dot, background: exInfoColor }} />
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{t('asterMonitor.layer3Label')}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{exInfoLabel}</span>
        </div>
      </div>

      <ExchangeAlertsSection exchangeAlerts={exchangeAlerts} isMobile={isMobile} />

      <div style={s.filters}>
        <div style={s.filterGroup}>
          <span style={s.filterLabel}>{t('asterMonitor.filterStage')}</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[{ value: '', label: t('asterMonitor.filterAll') }, ...Object.entries(STAGE).map(([v, m]) => ({ value: v, label: `${m.emoji} ${t(m.labelKey).split(' · ')[0]}` }))].map((o) => (
              <button key={o.value} onClick={(e) => { setStageFilter(o.value); e.currentTarget.blur(); }}
                style={stageFilter === o.value ? s.filterBtnActive : s.filterBtn}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={s.loading}>{t('asterMonitor.loadingData')}</div>
      ) : alerts.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚀</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
            {t('asterMonitor.emptyTitle')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 520, textAlign: 'center' }}>
            {t('asterMonitor.emptyDesc')}
          </div>
        </div>
      ) : isMobile ? (
        <div style={s.cards}>
          {alerts.map((a) => <AlertCard key={a._id} a={a} />)}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.thead}>
            <span style={{ ...s.th, flex: 1.6 }}>{t('asterMonitor.colStage')}</span>
            <span style={{ ...s.th, flex: 1.3 }}>{t('asterMonitor.colFunction')}</span>
            <span style={{ ...s.th, flex: 1.2 }}>{t('asterMonitor.colDetectedAt')}</span>
            <span style={{ ...s.th, flex: 1.6 }}>{t('asterMonitor.colSender')}</span>
            <span style={{ ...s.th, flex: 1.6 }}>{t('asterMonitor.colTx')}</span>
          </div>
          {alerts.map((a) => <AlertRow key={a._id} a={a} />)}
          <p style={s.note}>
            {t('asterMonitor.tableNote')}
          </p>
        </div>
      )}
    </div>
  );
}

// Whether the base asset is already listed on Binance/OKX — a token
// exclusive to Aster/HyperLiquid before a major CEX picks it up is the
// stronger signal, so this is shown as a badge row on every alert.
function CrossListingBadges({ crossListing }) {
  const { t } = useTranslation();
  if (!crossListing) return <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>—</span>;
  const hits = [
    crossListing.binance?.spot && 'Binance Spot',
    crossListing.binance?.futures && 'Binance Futures',
    crossListing.okx?.spot && 'OKX Spot',
    crossListing.okx?.swap && 'OKX Swap',
  ].filter(Boolean);
  if (hits.length === 0) {
    return <span style={{ ...s.sourceTag, color: '#10b981', borderColor: '#10b98155', background: '#10b98114' }}>{t('asterMonitor.notOnCex')}</span>;
  }
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {hits.map((h) => (
        <span key={h} style={{ ...s.sourceTag, color: '#f59e0b', borderColor: '#f59e0b55', background: '#f59e0b14' }}>{h}</span>
      ))}
    </div>
  );
}

const EX_STATUS_KEY = { TRADING: { labelKey: 'asterMonitor.statusTrading', color: '#10b981' }, PENDING_TRADING: { labelKey: 'asterMonitor.statusPendingTrading', color: '#f59e0b' } };

function ExchangeAlertsSection({ exchangeAlerts, isMobile }) {
  const { t } = useTranslation();
  if (!exchangeAlerts || exchangeAlerts.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={s.sectionHeader}>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>{t('asterMonitor.exchangeSectionTitle')}</span>
      </div>
      {isMobile ? (
        <div style={s.cards}>
          {exchangeAlerts.slice(0, 15).map((a) => <ExchangeAlertCard key={a._id} a={a} />)}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.thead}>
            <span style={{ ...s.th, flex: 1.2 }}>{t('asterMonitor.colSymbol')}</span>
            <span style={{ ...s.th, flex: 1 }}>{t('asterMonitor.colStatus')}</span>
            <span style={{ ...s.th, flex: 1.4 }}>{t('asterMonitor.colOnboardAt')}</span>
            <span style={{ ...s.th, flex: 2.2 }}>{t('asterMonitor.colOtherCex')}</span>
          </div>
          {exchangeAlerts.slice(0, 15).map((a) => {
            const st = EX_STATUS_KEY[a.status] ? { label: t(EX_STATUS_KEY[a.status].labelKey), color: EX_STATUS_KEY[a.status].color } : { label: a.status || '—', color: 'var(--text-dim)' };
            return (
              <div key={a._id} style={s.row}>
                <span style={{ ...s.td, flex: 1.2, fontWeight: 700, color: 'var(--text)' }}>{a.symbol}</span>
                <span style={{ ...s.td, flex: 1 }}>
                  <span style={{ ...s.badge, color: st.color, borderColor: st.color + '55', background: st.color + '14' }}>{st.label}</span>
                </span>
                <span style={{ ...s.td, flex: 1.4 }}>{fmtTime(a.onboardDate || a.detectedAt)}</span>
                <span style={{ ...s.td, flex: 2.2 }}><CrossListingBadges crossListing={a.crossListing} /></span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ExchangeAlertCard({ a }) {
  const { t } = useTranslation();
  const st = EX_STATUS_KEY[a.status] ? { label: t(EX_STATUS_KEY[a.status].labelKey), color: EX_STATUS_KEY[a.status].color } : { label: a.status || '—', color: 'var(--text-dim)' };
  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <span style={s.cardName}>{a.symbol}</span>
        <span style={{ ...s.badge, color: st.color, borderColor: st.color + '55', background: st.color + '14' }}>{st.label}</span>
      </div>
      <Cell label={t('asterMonitor.colOnboardAt')} value={fmtTime(a.onboardDate || a.detectedAt)} />
      <CrossListingBadges crossListing={a.crossListing} />
    </div>
  );
}

function AlertRow({ a }) {
  const { t } = useTranslation();
  const stage = STAGE[a.stage] || STAGE.pending;
  return (
    <div style={s.row}>
      <span style={{ ...s.td, flex: 1.6 }}>
        <span style={{ ...s.badge, color: stage.color, borderColor: stage.color + '55', background: stage.color + '14' }}>
          {stage.emoji} {t(stage.labelKey)}
        </span>
      </span>
      <span style={{ ...s.td, flex: 1.3, fontWeight: 600, color: 'var(--text)' }}>
        {FUNCTION_KEY[a.functionName] ? t(FUNCTION_KEY[a.functionName]) : a.functionName}
      </span>
      <span style={{ ...s.td, flex: 1.2 }}>{fmtTime(a.confirmedAt || a.minedAt || a.pendingAt || a.createdAt)}</span>
      <span style={{ ...s.td, flex: 1.6 }}>
        {a.fromAddress ? (
          <a href={addrUrl(a.fromAddress)} target="_blank" rel="noopener noreferrer" title={a.fromAddress} style={s.txLink}>
            {shortAddr(a.fromAddress)} ↗
          </a>
        ) : '—'}
      </span>
      <span style={{ ...s.td, flex: 1.6 }}>
        <a href={txUrl(a.txHash)} target="_blank" rel="noopener noreferrer" title={a.txHash} style={s.txLink}>
          {shortAddr(a.txHash)} ↗
        </a>
      </span>
    </div>
  );
}

function AlertCard({ a }) {
  const { t } = useTranslation();
  const stage = STAGE[a.stage] || STAGE.pending;
  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <span style={s.cardName}>{FUNCTION_KEY[a.functionName] ? t(FUNCTION_KEY[a.functionName]) : a.functionName}</span>
        <span style={{ ...s.badge, color: stage.color, borderColor: stage.color + '55', background: stage.color + '14' }}>
          {stage.emoji} {t(stage.labelKey).split(' · ')[0]}
        </span>
      </div>
      <div style={s.cardGrid}>
        <Cell label={t('asterMonitor.cardDetectedAt')} value={fmtTime(a.confirmedAt || a.minedAt || a.pendingAt || a.createdAt)} />
        <Cell label={t('asterMonitor.cardSender')} value={shortAddr(a.fromAddress)} />
      </div>
      <a href={txUrl(a.txHash)} target="_blank" rel="noopener noreferrer" title={a.txHash} style={{ ...s.cardMeta, ...s.txLink }}>
        {t('asterMonitor.cardTx')} {shortAddr(a.txHash)} ↗
      </a>
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 20 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  healthRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 },
  chip: { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '6px 12px', fontSize: 12 },
  dot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },

  filters: { display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  filterBtn: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', background: 'var(--surface)',
    outline: 'none', boxShadow: 'none',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--border)',
    borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
  },
  filterBtnActive: {
    fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent)14',
    outline: 'none', boxShadow: 'none',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--accent)',
    borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
  },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '8px 14px 14px', overflowX: 'auto' },
  thead: { display: 'flex', gap: 12, padding: '10px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 },
  th: { flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.7, textTransform: 'uppercase', minWidth: 0 },
  row: { display: 'flex', gap: 12, alignItems: 'center', padding: '11px 8px', borderBottom: '1px solid var(--border)', minWidth: 0 },
  td: { flex: 1, fontSize: 12, color: 'var(--text-dim)', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' },
  badge: { fontSize: 10, fontWeight: 700, border: '1px solid', borderRadius: 8, padding: '3px 9px', whiteSpace: 'nowrap', display: 'inline-block' },
  sourceTag: { fontSize: 9, fontWeight: 600, border: '1px solid', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', display: 'inline-block' },
  note: { fontSize: 10, color: 'var(--text-dim)', margin: '10px 0 0' },

  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
  cardName: { fontSize: 14, fontWeight: 700, color: 'var(--text)', wordBreak: 'break-word' },
  cardGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, minWidth: 0 },
  cardMeta: { fontSize: 10, color: 'var(--text-dim)' },
  txLink: { color: 'var(--accent)', textDecoration: 'none', display: 'inline-block' },
};
